import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { currentMonth, monthLabel, monthOptions, formatIDR } from "@/lib/month";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Target, Loader2, Plus, Download, Building2, Trash2, ArrowUp, ArrowDown, Minus, ChevronDown, ChevronRight, Lock, Unlock, FileDown, Landmark, Send } from "lucide-react";

const WEEK_KEYS = [1, 2, 3, 4];

function DeltaTag({ delta }) {
  if (delta === null || delta === undefined)
    return <span className="inline-flex items-center text-slate-400"><Minus className="h-3 w-3" /></span>;
  const up = delta >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${up ? "text-emerald-500" : "text-rose-500"}`} data-testid="week-delta">
      {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}{up ? "+" : ""}{Math.round(delta)}%
    </span>
  );
}

// Signed % for Final vs Target (variant). Positive = above target.
function VariantTag({ pct, dark }) {
  if (!pct && pct !== 0) return <span className={dark ? "text-slate-500" : "text-slate-400"}>–</span>;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${up ? (dark ? "text-emerald-400" : "text-emerald-600") : (dark ? "text-rose-400" : "text-rose-600")}`} data-testid="variant-pct">
      {up ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}{up ? "+" : ""}{pct}%
    </span>
  );
}

// ---- Billing contact inline editor (per property) ----
function PropertyBillingForm({ p, onSaved }) {
  const [vals, setVals] = useState({ billing_email: p.billing_email || "", billing_phone: p.billing_phone || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/properties/${p.id}`, vals);
      toast.success("Billing contact saved");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2 items-center pb-1">
      <Input value={vals.billing_email} onChange={(e) => setVals({ ...vals, billing_email: e.target.value })} placeholder="Billing email (untuk invoice)" className="h-8 text-xs flex-1" data-testid={`billing-email-${p.id}`} />
      <Input value={vals.billing_phone} onChange={(e) => setVals({ ...vals, billing_phone: e.target.value })} placeholder="Phone" className="h-8 text-xs w-28" data-testid={`billing-phone-${p.id}`} />
      <Button size="sm" className="h-8" onClick={save} disabled={busy} data-testid={`billing-save-${p.id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}</Button>
    </div>
  );
}

// ---- Manage properties (admin) ----
function PropertiesDialog({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [props, setProps] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", staff_ids: [], billing_email: "", billing_phone: "" });
  const [saving, setSaving] = useState(false);
  const [expand, setExpand] = useState(null);

  const load = useCallback(async () => {
    const [p, s] = await Promise.all([api.get("/properties"), api.get("/admin/staff")]);
    setProps(p.data.properties);
    setStaff(s.data.staff);
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const toggleFormStaff = (id) => setForm((f) => ({ ...f, staff_ids: f.staff_ids.includes(id) ? f.staff_ids.filter((x) => x !== id) : [...f.staff_ids, id] }));

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/properties", { name: form.name, billing_email: form.billing_email, billing_phone: form.billing_phone });
      if (form.staff_ids.length) await api.put(`/properties/${data.id}/staff`, { staff_ids: form.staff_ids });
      setForm({ name: "", staff_ids: [], billing_email: "", billing_phone: "" });
      await load();
      onChanged();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/properties/${id}`); await load(); onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const assignedIds = (pid) => staff.filter((s) => (s.property_ids || []).includes(pid)).map((s) => s.id);
  const togglePropStaff = async (pid, sid) => {
    const cur = new Set(assignedIds(pid));
    cur.has(sid) ? cur.delete(sid) : cur.add(sid);
    try { await api.put(`/properties/${pid}/staff`, { staff_ids: Array.from(cur) }); await load(); onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="manage-properties-btn"><Building2 className="h-4 w-4" /> Properties</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage properties</DialogTitle>
          <DialogDescription>Add hotel units and assign the staff who work at each one.</DialogDescription>
        </DialogHeader>
        <form onSubmit={add} className="space-y-2">
          <div className="space-y-1">
            <Label className="text-xs">Property name</Label>
            <div className="flex gap-2">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Villa Frangipani Seminyak" className="flex-1" data-testid="property-name-input" />
              <Button type="submit" disabled={saving} data-testid="add-property-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Billing email (untuk invoice)</Label>
              <Input type="email" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} placeholder="finance@villa.com" data-testid="property-billing-email-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Billing phone</Label>
              <Input value={form.billing_phone} onChange={(e) => setForm({ ...form, billing_phone: e.target.value })} placeholder="0812…" data-testid="property-billing-phone-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Assign staff (optional)</Label>
            <div className="max-h-28 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
              {staff.length === 0 && <p className="text-xs text-slate-400">No staff yet — add staff from the Monitoring page.</p>}
              {staff.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.staff_ids.includes(s.id)} onChange={() => toggleFormStaff(s.id)} data-testid={`new-prop-staff-${s.id}`} />{s.name}
                </label>
              ))}
            </div>
          </div>
        </form>
        <div className="space-y-1 mt-2">
          {props.map((p) => {
            const asg = assignedIds(p.id);
            const isOpen = expand === p.id;
            return (
              <div key={p.id} className="rounded-lg border border-slate-100">
                <div className="flex items-center justify-between text-sm py-1.5 px-2">
                  <button type="button" onClick={() => setExpand(isOpen ? null : p.id)} className="flex items-center gap-1 text-left flex-1" data-testid={`expand-prop-${p.id}`}>
                    {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    <span className="font-medium">{p.name}</span>
                    <span className="text-slate-400 text-xs">· {asg.length} staff</span>
                  </button>
                  <button onClick={() => del(p.id)} className="text-slate-400 hover:text-rose-500" data-testid={`delete-property-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                {isOpen && (
                  <div className="px-3 pb-2 space-y-1 border-t border-slate-100 pt-2">
                    <PropertyBillingForm p={p} onSaved={() => { load(); onChanged(); }} />
                    {staff.length === 0 && <p className="text-xs text-slate-400">No staff to assign.</p>}
                    {staff.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={asg.includes(s.id)} onChange={() => togglePropStaff(p.id, s.id)} data-testid={`assign-${p.id}-${s.id}`} />{s.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {props.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No properties yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Set target (admin) ----
function TargetDialog({ properties, month, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", revenue_target: "", profit_percent: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id) return toast.error("Select a property");
    setSaving(true);
    try {
      await api.post("/targets", { property_id: form.property_id, month, revenue_target: Number(form.revenue_target || 0), profit_percent: form.profit_percent === "" ? null : Number(form.profit_percent) });
      toast.success("Target saved");
      setForm({ property_id: "", revenue_target: "", profit_percent: "" });
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="set-target-btn"><Plus className="h-4 w-4" /> Set target</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set monthly target — {monthLabel(month)}</DialogTitle>
          <DialogDescription>Set the revenue target for a property this month.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Property</Label>
            <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
              <SelectTrigger data-testid="target-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
              <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Revenue target (IDR)</Label>
            <Input type="number" min="0" value={form.revenue_target} onChange={(e) => setForm({ ...form, revenue_target: e.target.value })} data-testid="target-revenue-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Profit margin (%)</Label>
            <Input type="number" min="0" step="0.1" value={form.profit_percent} onChange={(e) => setForm({ ...form, profit_percent: e.target.value })} placeholder="e.g. 30" data-testid="target-profit-input" />
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="save-target-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save target"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Bulk targets (admin) ----
function BulkTargetDialog({ properties, month, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);

  const loadExisting = useCallback(async () => {
    const { data } = await api.get(`/targets?month=${month}`);
    const map = {};
    data.targets.forEach((t) => { map[t.property_id] = { revenue_target: t.revenue_target || "", profit_percent: t.profit_percent || "" }; });
    setRows(map);
  }, [month]);
  useEffect(() => { if (open) loadExisting(); }, [open, loadExisting]);

  const upd = (pid, patch) => setRows((prev) => ({ ...prev, [pid]: { ...(prev[pid] || {}), ...patch } }));

  const submit = async () => {
    setSaving(true);
    try {
      const targets = properties.map((p) => ({
        property_id: p.id,
        revenue_target: Number(rows[p.id]?.revenue_target || 0),
        profit_percent: rows[p.id]?.profit_percent === "" || rows[p.id]?.profit_percent === undefined ? null : Number(rows[p.id].profit_percent),
      }));
      await api.post("/targets/bulk", { month, targets });
      toast.success("Targets saved for all properties");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="bulk-target-btn"><Plus className="h-4 w-4" /> Bulk targets</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk targets — {monthLabel(month)}</DialogTitle>
          <DialogDescription>Set revenue target and profit margin for every property in one screen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {properties.map((p) => (
            <div key={p.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`bulk-row-${p.id}`}>
              <span className="col-span-4 text-sm truncate">{p.name}</span>
              <Input className="col-span-5" type="number" min="0" placeholder="Target (IDR)" value={rows[p.id]?.revenue_target ?? ""} onChange={(e) => upd(p.id, { revenue_target: e.target.value })} data-testid={`bulk-target-${p.id}`} />
              <Input className="col-span-3" type="number" min="0" placeholder="Profit %" value={rows[p.id]?.profit_percent ?? ""} onChange={(e) => upd(p.id, { profit_percent: e.target.value })} data-testid={`bulk-profit-${p.id}`} />
            </div>
          ))}
          {properties.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No properties yet.</p>}
        </div>
        <Button onClick={submit} className="w-full" disabled={saving || properties.length === 0} data-testid="save-bulk-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save all targets"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function PerformancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dark = isAdmin;
  const [month, setMonth] = useState(currentMonth());
  const [grid, setGrid] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({}); // {property_id: {week: value}}
  const [propFilter, setPropFilter] = useState("all");
  const [staff, setStaff] = useState([]);
  const [staffFilter, setStaffFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get(`/performance/grid?month=${month}`)];
      if (isAdmin) { reqs.push(api.get("/properties")); reqs.push(api.get("/admin/staff")); }
      const res = await Promise.all(reqs);
      setGrid(res[0].data);
      if (isAdmin) { setProperties(res[1].data.properties); setStaff(res[2].data.staff); }
      setDraft({});
    } catch { toast.error("Failed to load performance"); }
    finally { setLoading(false); }
  }, [month, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const saveCell = async (pid, week, value) => {
    const num = Number(value || 0);
    try {
      await api.post("/achievements", { property_id: pid, month, week, revenue: num });
      toast.success(`Week ${week} saved`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveFinance = async (pid) => {
    const d = draft[pid] || {};
    const row = (grid?.rows || []).find((r) => r.property_id === pid);
    if (!row) return;
    const net = d.net !== undefined ? Number(d.net || 0) : (row.net_revenue || 0);
    const pct = d.pct !== undefined ? Number(d.pct || 0) : (row.profit_percent || 0);
    const bonus = d.bonus !== undefined ? Number(d.bonus || 0) : (row.bonus_percent || 0);
    try {
      await api.post("/targets/finance", { property_id: pid, month, net_revenue: net, profit_percent: pct, bonus_percent: bonus });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportRecap = async () => {
    try {
      const res = await api.get(`/admin/performance-export?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `performance_recap_${month}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Recap downloaded");
    } catch { toast.error("Export failed"); }
  };

  const exportPdf = () => {
    if (!grid) return;
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text(`BedsolvingOps - Monthly Report ${monthLabel(month)}`, 14, 14);
    doc.setFontSize(9);
    doc.setTextColor(130);
    doc.text("Seamless Workflow & Transparent Yield", 14, 20);
    autoTable(doc, {
      startY: 26,
      head: [["Property", "Target", "W1", "W2", "W3", "W4", "Final", "Achv %", "Variant", "Net Revenue", "Profit %", "Net Profit", "Bonus %", "Share Bonus"]],
      body: [
        ...rows.map((r) => [
          r.property_name, formatIDR(r.target), ...WEEK_KEYS.map((w) => formatIDR(r.weeks[w] || 0)),
          formatIDR(r.final), `${r.pct}%`, `${r.variant >= 0 ? "+" : ""}${formatIDR(r.variant)} (${r.variant_pct}%)`,
          formatIDR(r.net_revenue || 0), `${r.profit_percent || 0}%`, formatIDR(r.net_profit || 0),
          `${r.bonus_percent || 0}%`, formatIDR(r.bonus_amount || 0),
        ]),
        [
          "TOTAL", formatIDR(totals.target), ...WEEK_KEYS.map((w) => formatIDR(totals.weeks[w])),
          formatIDR(totals.final), `${totals.pct}%`, `${totals.final - totals.target >= 0 ? "+" : ""}${formatIDR(totals.final - totals.target)}`,
          formatIDR(rows.reduce((s, r) => s + (r.net_revenue || 0), 0)), "", formatIDR(rows.reduce((s, r) => s + (r.net_profit || 0), 0)),
          "", formatIDR(rows.reduce((s, r) => s + (r.bonus_amount || 0), 0)),
        ],
      ],
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [217, 102, 53] },
      didParseCell: (d) => { if (d.section === "body" && d.row.index === rows.length) d.cell.styles.fontStyle = "bold"; },
    });
    const splitRows = [];
    rows.forEach((r) => (r.staff_split || []).forEach((s) => splitRows.push([r.property_name, s.name, formatIDR(s.amount)])));
    if (splitRows.length) {
      doc.setFontSize(11);
      doc.setTextColor(30);
      doc.text("Staff Bonus Split", 14, doc.lastAutoTable.finalY + 8);
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 11,
        head: [["Property", "Staff", "Bonus Amount"]],
        body: splitRows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [31, 27, 22] },
      });
    }
    doc.save(`monthly_report_${month}.pdf`);
    toast.success("PDF downloaded");
  };

  const exportAccounting = async () => {
    try {
      const res = await api.get("/performance/accounting-export", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `accounting_import_${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Accounting CSV downloaded — siap di-import ke Rev.Bedsolving.id (Transaksi → Import)");
    } catch { toast.error("Export failed"); }
  };

  const [pushingId, setPushingId] = useState(null);
  const pushProperty = async (r) => {
    setPushingId(r.property_id);
    try {
      const { data } = await api.post("/admin/integrations/accounting/push", { month, property_id: r.property_id });
      toast.success(`Terkirim: ${data.property} · ${monthLabel(data.month)} · Net Profit Rp ${Number(data.net_profit).toLocaleString("id-ID")} — cek Rev.Bedsolving.id`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPushingId(null); }
  };

  const cellVal = (pid, week, fallback) => {
    const d = draft[pid]?.[week];
    return d !== undefined ? d : (fallback ?? "");
  };
  const setCell = (pid, week, v) => setDraft((prev) => ({ ...prev, [pid]: { ...(prev[pid] || {}), [week]: v } }));

  const gridLocked = grid?.locked;
  const toggleLock = async () => {
    try {
      await api.post("/bonus/lock", { month, locked: !gridLocked });
      toast.success(!gridLocked ? "Bonuses locked for the month" : "Bonuses unlocked");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const staffProps = staffFilter === "all" ? null : new Set((staff.find((s) => s.id === staffFilter)?.property_ids) || []);
  const allProps = grid
    ? grid.rows.filter((r) => !staffProps || staffProps.has(r.property_id)).map((r) => ({ id: r.property_id, name: r.property_name }))
    : [];
  const rows = grid
    ? grid.rows.filter((r) => (propFilter === "all" || r.property_id === propFilter) && (!staffProps || staffProps.has(r.property_id)))
    : [];
  const totals = (() => {
    const target = rows.reduce((s, r) => s + r.target, 0);
    const final = rows.reduce((s, r) => s + r.final, 0);
    const weeks = {}; let prev = null; const deltas = {};
    for (const w of WEEK_KEYS) weeks[w] = rows.reduce((s, r) => s + (r.weeks[w] || 0), 0);
    for (const w of WEEK_KEYS) { const v = weeks[w]; deltas[w] = prev === null ? null : (prev > 0 ? Math.round((v - prev) / prev * 100) : (v > 0 ? 100 : 0)); prev = v; }
    return {
      target, final, weeks, deltas,
      pct: target ? Math.round(final / target * 1000) / 10 : 0,
      variant: final - target,
      variant_pct: target ? Math.round((final - target) / target * 1000) / 10 : 0,
    };
  })();

  const headerCell = dark ? "text-slate-300" : "text-slate-600";
  const border = dark ? "border-slate-800" : "border-slate-200";

  return (
    <div className={`min-h-screen ${dark ? "bg-[#1F1B16] dark-scroll text-slate-100" : "bg-slate-50"}`}>
      <AppNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className={`font-display text-2xl font-bold flex items-center gap-2 ${dark ? "text-white" : "text-slate-900"}`}>
              <Target className="h-6 w-6 text-indigo-500" /> Performance & Targets
            </h1>
            <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>Property revenue vs {monthLabel(month)} target · weekly movement</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className={`w-40 ${dark ? "bg-slate-900 border-slate-700 text-slate-200" : ""}`} data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
            </Select>
            {allProps.length > 0 && (
              <Select value={propFilter} onValueChange={setPropFilter}>
                <SelectTrigger className={`w-48 ${dark ? "bg-slate-900 border-slate-700 text-slate-200" : ""}`} data-testid="property-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All properties</SelectItem>
                  {allProps.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {isAdmin && staff.length > 0 && (
              <Select value={staffFilter} onValueChange={(v) => { setStaffFilter(v); setPropFilter("all"); }}>
                <SelectTrigger className={`w-48 ${dark ? "bg-slate-900 border-slate-700 text-slate-200" : ""}`} data-testid="staff-filter"><SelectValue placeholder="All staff" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All staff</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {isAdmin && <PropertiesDialog onChanged={load} />}
            {isAdmin && <Button variant="outline" onClick={exportRecap} className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="export-recap-btn"><Download className="h-4 w-4" /> Export</Button>}
            {isAdmin && <Button variant="outline" onClick={exportPdf} className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="export-pdf-btn"><FileDown className="h-4 w-4" /> PDF</Button>}
            {isAdmin && <Button variant="outline" onClick={exportAccounting} className="bg-transparent border-amber-700/60 text-amber-300 hover:bg-slate-800" data-testid="export-accounting-btn"><Landmark className="h-4 w-4" /> Accounting</Button>}
            {isAdmin && <TargetDialog properties={properties} month={month} onSaved={load} />}
            {isAdmin && <BulkTargetDialog properties={properties} month={month} onSaved={load} />}
            {isAdmin && (
              <Button variant="outline" onClick={toggleLock} className={`bg-transparent hover:bg-slate-800 ${gridLocked ? "border-amber-500/50 text-amber-400" : "border-slate-700 text-slate-200"}`} data-testid="bonus-lock-btn">
                {gridLocked ? <><Unlock className="h-4 w-4" /> Unlock</> : <><Lock className="h-4 w-4" /> Lock bonuses</>}
              </Button>
            )}
          </div>
        </div>

        {loading || !grid ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-indigo-500" /></div>
        ) : grid.rows.length === 0 ? (
          <div className={`rounded-xl border ${border} p-10 text-center ${dark ? "bg-slate-900 text-slate-400" : "bg-white text-slate-500"}`}>
            {isAdmin ? "No properties yet. Click “Properties” to add your hotel units, then set targets." : "You are not assigned to any property yet. Please contact your admin."}
          </div>
        ) : (
          <div className={`rounded-xl border ${border} overflow-hidden ${dark ? "bg-slate-900" : "bg-white"}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="performance-grid">
                <thead>
                  <tr className={`border-b ${border} bg-amber-400/90 text-slate-900`}>
                    <th className="p-3 text-left font-bold min-w-[180px]">PROPERTY NAME</th>
                    <th className="p-3 text-right font-bold whitespace-nowrap">TARGET</th>
                    {WEEK_KEYS.map((w) => <th key={w} className="p-3 text-right font-bold whitespace-nowrap">WEEK {w}</th>)}
                    <th className="p-3 text-right font-bold">FINAL</th>
                    <th className="p-3 text-right font-bold">%</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.property_id} className={`border-b ${border} ${dark ? "hover:bg-slate-800/40" : "hover:bg-slate-50"}`} data-testid={`grid-row-${r.property_id}`}>
                      <td className={`p-3 font-medium ${dark ? "text-white" : "text-slate-900"}`}>{r.property_name}</td>
                      <td className={`p-3 text-right font-mono ${headerCell}`}>{formatIDR(r.target)}</td>
                      {WEEK_KEYS.map((w) => (
                        <td key={w} className="p-2 text-right align-top">
                          <Input
                            type="number" min="0"
                            className={`h-8 text-right text-xs font-mono ${dark ? "bg-slate-800 border-slate-700" : ""}`}
                            value={cellVal(r.property_id, w, r.weeks[w] || "")}
                            onChange={(e) => setCell(r.property_id, w, e.target.value)}
                            onBlur={(e) => {
                              const orig = r.weeks[w] || 0;
                              if (Number(e.target.value || 0) !== orig) saveCell(r.property_id, w, e.target.value);
                            }}
                            data-testid={`cell-${r.property_id}-${w}`}
                          />
                          <div className="text-right mt-0.5 pr-1"><DeltaTag delta={r.deltas[w]} /></div>
                        </td>
                      ))}
                      <td className="p-2 text-right align-top">
                        <Input
                          type="number" min="0"
                          className={`h-8 text-right text-xs font-mono font-semibold ${dark ? "bg-slate-800 border-slate-700" : ""}`}
                          value={cellVal(r.property_id, 0, r.final || "")}
                          onChange={(e) => setCell(r.property_id, 0, e.target.value)}
                          onBlur={(e) => { if (Number(e.target.value || 0) !== (r.final || 0)) saveCell(r.property_id, 0, e.target.value); }}
                          data-testid={`final-${r.property_id}`}
                        />
                        {r.final_manual && <div className="text-[10px] text-amber-500 text-right pr-1">manual</div>}
                      </td>
                      <td className="p-3 text-right">
                        <VariantTag pct={r.variant_pct} dark={dark} />
                        <div className={`text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>achieved {r.pct}%</div>
                      </td>
                    </tr>
                  ))}
                  {/* totals */}
                  <tr className={`${dark ? "bg-slate-800/60" : "bg-slate-100"} font-semibold`} data-testid="grid-total-row">
                    <td className={`p-3 ${dark ? "text-white" : "text-slate-900"}`}>TOTAL</td>
                    <td className={`p-3 text-right font-mono ${dark ? "text-white" : "text-slate-900"}`}>{formatIDR(totals.target)}</td>
                    {WEEK_KEYS.map((w) => (
                      <td key={w} className={`p-3 text-right font-mono ${dark ? "text-slate-200" : "text-slate-700"}`}>
                        <div>{formatIDR(totals.weeks[w])}</div>
                        <div className="text-right"><DeltaTag delta={totals.deltas[w]} /></div>
                      </td>
                    ))}
                    <td className={`p-3 text-right font-mono ${dark ? "text-white" : "text-slate-900"}`}>{formatIDR(totals.final)}</td>
                    <td className="p-3 text-right">
                      <VariantTag pct={totals.variant_pct} dark={dark} />
                      <div className={`text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>achieved {totals.pct}%</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className={`text-xs px-3 py-2 ${dark ? "text-slate-500" : "text-slate-400"}`}>
              Type a weekly revenue figure and click away to save. The small % under each week shows the movement vs the previous week.
            </p>
          </div>
        )}

        {/* Comparison: target vs final, variant & profit */}
        {!loading && grid && grid.rows.length > 0 && (
          <div className={`rounded-xl border ${border} overflow-hidden mt-6 ${dark ? "bg-slate-900" : "bg-white"}`}>
            <div className={`px-4 py-3 border-b ${border}`}>
              <h3 className={`font-display font-semibold ${dark ? "text-white" : "text-slate-900"}`}>Monthly comparison — {monthLabel(month)}</h3>
              <p className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>Target &amp; final pull from weekly monitoring. Net revenue, profit %, and bonus % are manual inputs — net profit = net revenue × profit %, and share bonus = net profit × bonus %.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead>
                  <tr className={`border-b ${border} ${dark ? "text-slate-400" : "text-slate-500"} text-left`}>
                    <th className="p-3 font-medium min-w-[180px]">Property</th>
                    <th className="p-3 font-medium text-right">Target</th>
                    <th className="p-3 font-medium text-right">Final</th>
                    <th className="p-3 font-medium text-right">Variant</th>
                    <th className="p-3 font-medium text-right">Net Revenue</th>
                    <th className="p-3 font-medium text-right">Profit %</th>
                    <th className="p-3 font-medium text-right">Net Profit</th>
                    <th className="p-3 font-medium text-right">Bonus %</th>
                    <th className="p-3 font-medium text-right">Share Bonus</th>
                    {isAdmin && <th className="p-3 font-medium text-right">Push</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.property_id} className={`border-b ${border} ${dark ? "hover:bg-slate-800/40" : "hover:bg-slate-50"}`} data-testid={`compare-row-${r.property_id}`}>
                      <td className={`p-3 font-medium ${dark ? "text-white" : "text-slate-900"}`}>{r.property_name}</td>
                      <td className={`p-3 text-right font-mono ${headerCell}`}>{formatIDR(r.target)}</td>
                      <td className={`p-3 text-right font-mono ${dark ? "text-slate-200" : "text-slate-700"}`}>{formatIDR(r.final)}</td>
                      <td className={`p-3 text-right font-mono font-semibold ${r.variant >= 0 ? "text-emerald-500" : "text-rose-500"}`} data-testid={`variant-${r.property_id}`}>
                        {r.variant >= 0 ? "+" : ""}{formatIDR(r.variant)}
                        <span className={`block text-[10px] font-semibold ${r.variant_pct >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{r.variant_pct >= 0 ? "+" : ""}{r.variant_pct}%</span>
                      </td>
                      <td className="p-2 text-right">
                        <Input type="number" min="0" disabled={gridLocked} className={`h-8 text-right text-xs font-mono ${dark ? "bg-slate-800 border-slate-700" : ""}`} value={cellVal(r.property_id, "net", r.net_revenue || "")} onChange={(e) => setCell(r.property_id, "net", e.target.value)} onBlur={() => saveFinance(r.property_id)} data-testid={`netrev-${r.property_id}`} />
                      </td>
                      <td className="p-2 text-right">
                        <Input type="number" min="0" disabled={gridLocked} className={`h-8 text-right text-xs font-mono w-20 ml-auto ${dark ? "bg-slate-800 border-slate-700" : ""}`} value={cellVal(r.property_id, "pct", r.profit_percent || "")} onChange={(e) => setCell(r.property_id, "pct", e.target.value)} onBlur={() => saveFinance(r.property_id)} data-testid={`profitpct-${r.property_id}`} />
                      </td>
                      <td className={`p-3 text-right font-mono font-semibold ${dark ? "text-indigo-300" : "text-indigo-600"}`} data-testid={`profit-${r.property_id}`}>{formatIDR(r.net_profit)}</td>
                      <td className="p-2 text-right">
                        <Input type="number" min="0" disabled={gridLocked} className={`h-8 text-right text-xs font-mono w-20 ml-auto ${dark ? "bg-slate-800 border-slate-700" : ""}`} value={cellVal(r.property_id, "bonus", r.bonus_percent || "")} onChange={(e) => setCell(r.property_id, "bonus", e.target.value)} onBlur={() => saveFinance(r.property_id)} data-testid={`bonuspct-${r.property_id}`} />
                      </td>
                      <td className="p-3 text-right" data-testid={`bonus-${r.property_id}`}>
                        <span className={`font-mono font-semibold ${dark ? "text-emerald-300" : "text-emerald-600"}`}>{formatIDR(r.bonus_amount)}</span>
                        <span className={`block text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>{r.share_bonus_pct}% of total profit</span>
                        {(r.staff_split || []).length > 0 && (
                          <span className={`block text-[10px] ${dark ? "text-slate-400" : "text-slate-500"}`}>{r.staff_split.map((s) => `${s.name.split(" ")[0]} ${formatIDR(s.amount)}`).join(" · ")}</span>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => pushProperty(r)}
                            disabled={pushingId === r.property_id || !(r.net_profit > 0)}
                            title={r.net_profit > 0 ? `Push ${r.property_name} (${monthLabel(month)}) ke Rev.Bedsolving.id` : "Isi Net Revenue & Profit % dulu"}
                            className="bg-transparent border-emerald-700/60 text-emerald-300 hover:bg-slate-800"
                            data-testid={`push-${r.property_id}`}
                          >
                            {pushingId === r.property_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                  <tr className={`${dark ? "bg-slate-800/60" : "bg-slate-100"} font-semibold`}>
                    <td className={`p-3 ${dark ? "text-white" : "text-slate-900"}`}>TOTAL</td>
                    <td className={`p-3 text-right font-mono ${dark ? "text-white" : "text-slate-900"}`}>{formatIDR(totals.target)}</td>
                    <td className={`p-3 text-right font-mono ${dark ? "text-white" : "text-slate-900"}`}>{formatIDR(totals.final)}</td>
                    <td className={`p-3 text-right font-mono ${totals.final - totals.target >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
                      {totals.final - totals.target >= 0 ? "+" : ""}{formatIDR(totals.final - totals.target)}
                      <span className={`block text-[10px] ${totals.variant_pct >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{totals.variant_pct >= 0 ? "+" : ""}{totals.variant_pct}%</span>
                    </td>
                    <td className={`p-3 text-right font-mono ${dark ? "text-slate-200" : "text-slate-700"}`}>{formatIDR(rows.reduce((s, r) => s + (r.net_revenue || 0), 0))}</td>
                    <td className="p-3"></td>
                    <td className={`p-3 text-right font-mono ${dark ? "text-indigo-300" : "text-indigo-600"}`}>{formatIDR(rows.reduce((s, r) => s + (r.net_profit || 0), 0))}</td>
                    <td className="p-3"></td>
                    <td className={`p-3 text-right font-semibold ${dark ? "text-emerald-300" : "text-emerald-600"}`}>{formatIDR(rows.reduce((s, r) => s + (r.bonus_amount || 0), 0))}</td>
                    {isAdmin && <td className="p-3"></td>}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
